// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel from "@conklin/App/Components/XngCarousel";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { SLIDES_PER_VIEW_POINTS } from "@conklin/XDL/constants";
import { generateSlidesPerViewMap, getDataLayer } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    // XNG-Change
    view = "view",
    showScrollbar = "showScrollbar",
    isGreyBackground = "isGreyBackground",
    maximumNumberOfBrands = "maximumNumberOfBrands",
    displayAsCarouselOnMobile = "displayAsCarouselOnMobile",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        // XNG-Change
        [fields.view]: string;
        [fields.showScrollbar]: boolean;
        [fields.isGreyBackground]: boolean;
        [fields.maximumNumberOfBrands]: number;
        [fields.displayAsCarouselOnMobile]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(
            // XNG-Change
            props.fields.brandsToDisplay === "random"
                ? props.fields.numberOfBrandsToDisplay
                : props.fields.maximumNumberOfBrands?.toString() || "0",
            10,
        ),
        // XNG-Change
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText,properties,productListPagePath",
    };

    // XNG-Change
    if (!brandGalleryParameter.randomize && brandGalleryParameter.brandIds.length < brandGalleryParameter.pageSize) {
        (brandGalleryParameter as any).fallbackBrands = true;
    }

    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    // XNG-Change
    const shouldLoadBrandGallery = !brandsState.isLoading;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        // XNG-Change
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        notFoundBrandImagePath: (getSettingsCollection(state)?.imageKitIOSettings as any)?.brandFallBackImage,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & HasHistory;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // XNG-Change
    carouselImage?: LazyImageProps;
    slide?: InjectableCss;
    brandsContainer?: InjectableCss;
    brandItemWrapper?: InjectableCss;
    brandName?: TypographyProps;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
};

const styles = listStyles;

// XNG-Changes: refractor to functional component
const BrandGallery: FC<Props> = ({
    brandsState: { isLoading, value: brandList },
    fields,
    history,
    loadBrandGallery,
    shouldLoadBrandGallery,
    brandGalleryParameter,
    // XNG-Changes
    isTabletScreen,
    notFoundBrandImagePath,
}: Props) => {
    // XNG-Changes
    const [brandsPerRow, setbrandsPerRow] = React.useState(fields.maximumNumberOfBrandsPerRow);

    // XNG-Changes
    React.useEffect(() => {
        setbrandsPerRow(fields.maximumNumberOfBrandsPerRow);
    }, [fields.maximumNumberOfBrandsPerRow]);

    // XNG-Changes
    React.useEffect(() => {
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }, [fields.maximumNumberOfBrands, fields.numberOfBrandsToDisplay, fields.brandsToDisplay, fields.brandsSelected]);

    if (isLoading || !brandList) {
        return null;
    }

    const isCarouselView = fields.view === "carousel";

    // XNG-Changes
    const desktop = parseInt(brandsPerRow, 10);
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${fields.maximumBrandImageHeight}px`,
        },
    };

    const focusFunction = () => {
        const elementToFocus = document.getElementById("collapseButton");
        if (elementToFocus) {
            elementToFocus.focus();
        } else {
            document.getElementById("expandButton")?.focus();
        }
    };

    // XNG-Change
    const trackBrandClick = (brand: BrandModel) => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            const googleAnalyticsData = {
                event: "shop_all_manufacturers",
                text: brand.name,
                destination: brand.productListPagePath,
            };
            dataLayer.push(googleAnalyticsData);
        }
        history.push(brand.productListPagePath);
    };

    // XNG-Changes
    const getMin = (num: number) => {
        return Math.min(num, parseInt(brandsPerRow || "6", 10));
    };

    // XNG-Changes: adding break points
    const breakpoints = {
        320: {
            slidesPerView: isCarouselView ? getMin(1.5) : getMin(2),
        },
        500: {
            slidesPerView: getMin(2),
        },
        768: {
            slidesPerView: isCarouselView ? getMin(3) : getMin(4.3),
        },
        1025: {
            slidesPerView: isCarouselView ? getMin(4) : getMin(4.3),
        },
        1200: {
            slidesPerView: getMin(5.7),
        },
        1601: {
            slidesPerView: getMin(6.8),
        },
    };

    // XNG-Changes
    const uid = uniqueId();

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandGallery">
            <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
            {fields.title && (
                <Typography variant="headerSecondary" {...styles.titleText} data-test-selector="brandGalleryTitle">
                    {fields.title}
                </Typography>
            )}
            {/* XNG-Change: use carousel */}
            {(isCarouselView && !isTabletScreen) || (isTabletScreen && fields.displayAsCarouselOnMobile) ? (
                <XngCarousel
                    spaceBetween={isCarouselView ? 36 : 16}
                    navigation={!isTabletScreen}
                    draggable={isTabletScreen}
                    scrollbar={fields.showScrollbar}
                    className={fields.isGreyBackground ? "xng-swiper-with-grey-shadow" : "xng-swiper-with-shadow"}
                    // XNG-Changes
                    breakpoints={breakpoints}
                    arrowNavigationType="circle"
                >
                    {brandList.map(brand => (
                        <StyledWrapper {...styles.slide} key={brand.id}>
                            {brand === brandList[0] && (
                                <SkipNav
                                    extendedStyles={styles.skipToContent}
                                    text={translate("Skip to full brand list")}
                                    focusFunction={focusFunction}
                                />
                            )}
                            <Clickable onClick={() => trackBrandClick(brand)} {...styles.link}>
                                <LazyImage
                                    width="auto"
                                    height={!isCarouselView ? "156px" : "100%"}
                                    imgProps={imgProps}
                                    {...styles.carouselImage}
                                    src={brand.logoSmallImagePath || brand.logoLargeImagePath || notFoundBrandImagePath}
                                    altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                                />
                                {!isCarouselView && (
                                    <Typography {...styles.brandName}>{translate(brand.name)}</Typography>
                                )}
                            </Clickable>
                        </StyledWrapper>
                    ))}
                </XngCarousel>
            ) : (
                <FlexWrapContainer {...styles.imageContainer}>
                    {brandList.map((brand, index) => (
                        <>
                            <FlexItem
                                // XNG-Changes: Each child in a list should have a unique "key" prop.
                                key={brand.id + uid}
                                flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                {...styles.imageItem}
                                {...{ width: fields.maximumBrandImageHeight || 204 }}
                            >
                                {brand === brandList[0] && (
                                    <SkipNav
                                        extendedStyles={styles.skipToContent}
                                        text={translate("Skip to full brand list")}
                                        focusFunction={focusFunction}
                                    />
                                )}
                                {/* XNG-Change */}
                                <Clickable onClick={() => trackBrandClick(brand)} {...styles.link} key={brand.id}>
                                    <StyledWrapper
                                        {...styles.brandItemWrapper}
                                        {...{ width: fields.maximumBrandImageHeight || 204 }}
                                    >
                                        <LazyImage
                                            width={`${fields.maximumBrandImageHeight}` || "204"}
                                            height={`${fields.maximumBrandImageHeight}` || "204"}
                                            {...styles.image}
                                            src={brand.logoSmallImagePath || notFoundBrandImagePath}
                                            altText={`${translate("Go to brand page")} ${
                                                brand.logoAltText || brand.name
                                            }`}
                                        />
                                    </StyledWrapper>
                                    <Typography {...styles.brandName}>{translate(brand.name)}</Typography>
                                </Clickable>
                            </FlexItem>
                        </>
                    ))}
                </FlexWrapContainer>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(BrandGallery)),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                horizontal: true,
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
            },
            // XNG-Change
            {
                name: fields.maximumNumberOfBrands,
                displayName: "Maximum Number of Brands",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 40,
                fieldType: "Contextual",
                isRequired: true,
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
            },
            // XNG-Change
            {
                name: fields.view,
                displayName: "Widget View",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Grid",
                        value: "grid",
                    },
                    {
                        displayName: "Carousel",
                        value: "carousel",
                    },
                ],
                defaultValue: "carousel",
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "Contextual",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
                tooltipPosition: "left",
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            // XNG-Change
            {
                name: fields.displayAsCarouselOnMobile,
                displayName: "Display as Carousel on Mobile",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.showScrollbar,
                displayName: "Show scrollbar",
                tooltip: "Show scrollbar on small screens and carousel view",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            // XNG-Change
            {
                name: fields.isGreyBackground,
                displayName: "Carousel has Grey Background",
                tooltip: "Controls the color of shadow",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isVisible: item => item.fields.view === "carousel",
            },
        ],
    },
};

export default widgetModule;
